import { useMutation, useQuery } from "@tanstack/react-query";
import { useGet, usePost, usePut } from "../../../common/api-service";
import useMainStore from "../../landing-page/api/store";
import { AssignedProductsModel, InternalProductModel, PagedUserManagementModel } from "./types";
import useUserManagementStore from "./store";
import queryClient from "../../../common/query-client";
import { signFile } from "../../../common/utils/file-signing";
import { UserFormSchema } from "../../../common/components/user-inv-modal/api/schema";
import useSuccessModalStore from "../../../common/components/success-modal/api/store";
import { AppSnackBarHandlers } from "../../../common/components/app-snackbar/handlers";
import { dummyPagedUserManagement } from "./walkthrough";

export const useGetPagedUserManagementUsers = (
  isInternal: boolean,
  walkthroughMode: boolean, // <-- Add walkthroughMode param
) => {
  const { get } = useGet();
  const mainStore = useMainStore();
  const { initialUserManagementState } = useUserManagementStore();
  const { pageNumber, pageSize, searchText } = initialUserManagementState;
  const clientId = mainStore.selectedClient?.id;

  let url =
    `/api/user-management/list` +
    `?PageNumber=${pageNumber}` +
    `&PageSize=${pageSize}` +
    `&IsInternal=${isInternal}`;

  if (searchText) {
    url += `&SearchText=${encodeURIComponent(searchText)}`;
  }
  if (clientId) {
    url += `&ClientId=${encodeURIComponent(clientId)}`;
  }

  return useQuery<PagedUserManagementModel>({
    queryFn: () =>
      walkthroughMode
        ? Promise.resolve(dummyPagedUserManagement) // <-- Return dummy data if walkthroughMode
        : get<PagedUserManagementModel>(url),
    queryKey: [
      "userManagement",
      {
        pageNumber,
        pageSize,
        searchText,
        clientId,
        isInternal,
        walkthroughMode,
      }, // <-- Add to cache key
    ],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
    enabled: !!mainStore.currentUser || walkthroughMode,
  });
};
export const useGetUserById = (isInternal: boolean) => {
  const { get } = useGet();
  const { selectedUserId, isEditModalOpen, isEditGroupAdminModal } =
    useUserManagementStore();

  const userId = selectedUserId ?? "";
  let url =
    `/api/user-management/get-user-by-id` +
    `?UserId=${encodeURIComponent(userId)}` +
    `&IsInternal=${isInternal}`;

  return useQuery<UserFormSchema>({
    queryFn: () => get<UserFormSchema>(url),
    queryKey: ["userManagement", { userId, isInternal }],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
    enabled: isEditGroupAdminModal || isEditModalOpen,
  });
};

export const useCreateBizboxUser = (isInternal: boolean) => {
  const { post } = usePost();
  const { initialUserManagementState, setAddModal } = useUserManagementStore();
  const mainStore = useMainStore();
  const isGroupAdmin = mainStore.currentUser?.roles?.includes("GroupAdmin");
  const selectedIds = useUserManagementStore(
    (state) => state.selectedGroupClientIds,
  );
  const { setShowModal } = useSuccessModalStore();

  return useMutation({
    mutationFn: async (data: UserFormSchema & { file?: File }) => {
      const formData = new FormData();

      if (!isInternal && mainStore.selectedClient?.id) {
        formData.append("ClientId", mainStore.selectedClient.id);
      }

      formData.append("User.Email", data.email);
      formData.append("User.FirstName", data.firstName);
      formData.append("User.LastName", data.lastName);
      if (data.birthDate) formData.append("User.BirthDate", data.birthDate);
      formData.append("User.Role.Id", data.role.id);
      formData.append("User.Role.Role", data.role.role.toString());

      if (data.id) {
        formData.append("User.Id", data.id);
      }
      if (data.middleName) {
        formData.append("User.MiddleName", data.middleName);
      }
      if (data.prefix) {
        formData.append("User.Prefix", data.prefix);
      }
      if (data.suffix) {
        formData.append("User.Suffix", data.suffix);
      }
      if (data.customName) {
        formData.append("User.CustomName", data.customName);
      }
      if (data.civilStatus !== undefined) {
        formData.append("User.CivilStatus", data.civilStatus.toString());
      }
      if (data.gender !== undefined) {
        formData.append("User.Gender", data.gender.toString());
      }
      if (data.department !== undefined && data.department !== null) {
        formData.append("User.Department", data.department.toString());
      }
      if (data.productCode) {
        formData.append("User.ProductCode", data.productCode);
      }
      if (data.facilityCode) {
        formData.append("User.FacilityCode", data.facilityCode);
      }

      if (data.photoUrl instanceof File) {
        const signature = await signFile(data.photoUrl);
        formData.append("UserPhoto", data.photoUrl, data.photoUrl.name);
        formData.append("UserPhotoSignature", signature);
      }

      formData.append("IsInternal", isInternal.toString());

      return post<FormData, UserFormSchema>(
        `/api/user-management/create?IsInternal=${isInternal}`,
        formData,
        true,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
    },
    onSuccess: (data: UserFormSchema) => {
      setAddModal(false);
      if (data.id === null) {
        setShowModal(true);
      }
      queryClient.invalidateQueries({
        queryKey: [
          "userManagement",
          {
            pageNumber: initialUserManagementState.pageNumber,
            pageSize: initialUserManagementState.pageSize,
            searchText: initialUserManagementState.searchText,
            isInternal,
          },
        ],
      });
    },
  });
};

export const useUpdateBizboxUser = (isInternal: boolean) => {
  const { put } = usePut();
  const {
    initialUserManagementState,
    setEditModalOpen,
    selectedGroupClientIds: selectedIds,
  } = useUserManagementStore();
  const mainStore = useMainStore();
  const isGroupAdmin = mainStore.currentUser?.roles?.includes("GroupAdmin");

  return useMutation<unknown, Error, UserFormSchema>({
    mutationFn: async (data) => {
      const formData = new FormData();

      formData.append("User.Id", data.id ?? "");
      formData.append("User.Email", data.email);
      formData.append("User.FirstName", data.firstName);
      formData.append("User.LastName", data.lastName);
      if (data.birthDate) formData.append("User.BirthDate", data.birthDate);
      formData.append("User.Role.Id", data.role.id);
      formData.append("User.Role.Role", data.role.role.toString());

      if (data.middleName) formData.append("User.MiddleName", data.middleName);
      if (data.prefix) formData.append("User.Prefix", data.prefix);
      if (data.suffix) formData.append("User.Suffix", data.suffix);
      if (data.customName) formData.append("User.CustomName", data.customName);
      if (data.civilStatus !== undefined)
        formData.append("User.CivilStatus", data.civilStatus.toString());
      if (data.gender !== undefined)
        formData.append("User.Gender", data.gender.toString());
      if (data.department !== undefined && data.department !== null)
        formData.append("User.Department", data.department.toString());
      if (data.productCode)
        formData.append("User.ProductCode", data.productCode);
      if (data.facilityCode)
        formData.append("User.FacilityCode", data.facilityCode);

      if (!isInternal && mainStore.selectedClient?.id) {
        formData.append("ClientId", mainStore.selectedClient.id);
      }

      if (data.photoUrl instanceof File) {
        const signature = await signFile(data.photoUrl);
        formData.append("UserPhoto", data.photoUrl, data.photoUrl.name);
        formData.append("UserPhotoSignature", signature);
      }

      formData.append("IsInternal", isInternal.toString());

      return put<FormData, UserFormSchema>(
        `/api/user-management/update?IsInternal=${isInternal}`,
        formData,
        true,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
    },
    onSuccess: () => {
      setEditModalOpen(false);

      queryClient.invalidateQueries({
        queryKey: [
          "userManagement",
          {
            pageNumber: initialUserManagementState.pageNumber,
            pageSize: initialUserManagementState.pageSize,
            searchText: initialUserManagementState.searchText,
            clientId: mainStore.selectedClient?.id,
            isInternal,
          },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["get-user-by-id", selectedIds],
      });
    },
  });
};

interface DeleteBizboxUserPayload {
  UserId: string;
  Reason: string;
}

export const useDeleteBizboxUser = () => {
  const { put } = usePut();
  const { initialUserManagementState, activeUser } = useUserManagementStore();
  const mainStore = useMainStore();

  return useMutation<unknown, Error, string>({
    mutationFn: (reason: string) => {
      const payload: DeleteBizboxUserPayload = {
        UserId: activeUser?.applicationUserId ?? "",
        Reason: reason,
      };
      return put<DeleteBizboxUserPayload, unknown>(
        `/api/user-management/delete-user`,
        payload,
        true,
      );
    },
    onSuccess: () => {
      const { pageNumber, pageSize, searchText } = initialUserManagementState;
      const clientId = mainStore.selectedClient?.id;
      queryClient.invalidateQueries({
        queryKey: [
          "userManagement",
          { pageNumber, pageSize, searchText, clientId },
        ],
      });
    },
  });
};

interface LockUserPayload {
  UserId: string;
  AdminLockingReason: string;
  IsInternal: boolean;
}

export const useGetInternalProducts = () => {
  const { get } = useGet();
  return useQuery<InternalProductModel[]>({
    queryFn: () => get<InternalProductModel[]>("/api/user-management/internal-products"),
    queryKey: ["internal-products"],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useGetAssignedProducts = (userId: string | null) => {
  const { get } = useGet();
  return useQuery<AssignedProductsModel>({
    queryFn: () =>
      get<AssignedProductsModel>(
        `/api/user-management/assigned-products?UserId=${encodeURIComponent(userId ?? "")}`,
      ),
    queryKey: ["assigned-products", userId],
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 0,
    enabled: !!userId,
  });
};

export const useAssignProducts = () => {
  const { put } = usePut();
  const { setAssignProductsModal } = useUserManagementStore();
  const appSnackBarHandlers = new AppSnackBarHandlers();

  return useMutation<unknown, Error, { userId: string; productIds: number[] }>({
    mutationFn: ({ userId, productIds }) =>
      put<{ UserId: string; ProductIds: number[] }, unknown>(
        "/api/user-management/assign-products",
        { UserId: userId, ProductIds: productIds },
        true,
      ),
    onSuccess: (_data, { userId }) => {
      setAssignProductsModal(false);
      queryClient.invalidateQueries({ queryKey: ["assigned-products", userId] });
      appSnackBarHandlers.handlePopIn("Product assignments updated", "", false);
    },
  });
};

export const useLockUser = (isInternal: boolean) => {
  const { put } = usePut();
  const mainStore = useMainStore();
  const { initialUserManagementState } = useUserManagementStore();
  const appSnackBarHandlers = new AppSnackBarHandlers();

  return useMutation<
    string,
    Error,
    { userId: string; adminLockingReason: string }
  >({
    mutationFn: async ({ userId, adminLockingReason }) => {
      const payload: LockUserPayload = {
        UserId: userId,
        AdminLockingReason: adminLockingReason,
        IsInternal: isInternal,
      };
      return put<LockUserPayload, string>(
        `/api/user-management/lock-user`,
        payload,
        adminLockingReason === "" ? false : true,
      );
    },
    onSuccess: (_data, { adminLockingReason }) => {
      if (adminLockingReason === "") {
        appSnackBarHandlers.handlePopIn(
          "Account successfully unlocked",
          "",
          false,
        );
      }

      const { pageNumber, pageSize, searchText } = initialUserManagementState;
      const clientId = mainStore.selectedClient?.id;
      queryClient.invalidateQueries({
        queryKey: [
          "userManagement",
          { pageNumber, pageSize, searchText, clientId, isInternal },
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [
          "userManagement",
          {
            pageNumber: initialUserManagementState.pageNumber,
            pageSize: initialUserManagementState.pageSize,
            searchText: initialUserManagementState.searchText,
            clientId: mainStore.selectedClient?.id,
            isInternal,
          },
        ],
      });
    },
  });
};
